import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Mail, MessageSquare } from "lucide-react";
import { fetchCommsAnalytics } from "../../communications/api/communicationsApi";
import LinkClicksPanel from "./LinkClicksPanel";

function StatCard({ label, value, sub }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

/**
 * Communications Hub roll-up, rendered inside the main Analytics page.
 * Reads /api/communications/analytics/summary/ (same endpoint the Hub used).
 */
export default function CommunicationsAnalyticsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCommsAnalytics()
      .then(setData)
      .catch(() => toast.error("Couldn't load communications analytics"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="text-gray-400 text-sm p-6">Loading…</p>;
  if (!data) return <p className="text-gray-400 text-sm p-6">No data.</p>;

  return (
    <div>
      {!data.send_enabled && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-800">
          <strong>Sending is OFF (safe mode)</strong> - campaigns are simulated, so counts below reflect test/simulated sends only.
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        <StatCard label="Campaigns" value={data.campaign_count} />
        <StatCard label="Sent" value={data.sent} />
        <StatCard label="Delivered" value={data.delivered} />
        <StatCard label="Open Rate" value={`${data.open_rate}%`} sub={`${data.opened} opens`} />
        <StatCard label="Click Rate" value={`${data.click_rate}%`} sub={`${data.clicked} clicks`} />
        <StatCard label="Bounced" value={data.bounced} />
        <StatCard label="Unsubscribed" value={data.unsubscribed} />
        <StatCard label="Failed" value={data.failed} />
      </div>

      <h3 className="font-bold text-gray-800 mt-6 mb-2">Top Campaigns</h3>
      <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
        {(data.top_campaigns || []).map((c) => (
          <div key={c.id} className="flex justify-between items-center px-4 py-3 text-sm">
            <span className="flex items-center gap-2 font-medium text-gray-700">
              {c.channel === "sms" ? (
                <MessageSquare className="w-4 h-4 text-purple-500" />
              ) : (
                <Mail className="w-4 h-4 text-blue-500" />
              )}
              {c.name}
            </span>
            <span className="text-gray-400">
              {c.opened_count} opens · {c.clicked_count} clicks
            </span>
          </div>
        ))}
        {(data.top_campaigns || []).length === 0 && (
          <p className="text-gray-400 text-sm px-4 py-3">No sent campaigns yet.</p>
        )}
      </div>

      {/* WHICH buyers clicked the two closing lines, and the one-press portal
          invite for anyone who tapped "join the Off Market Portal" and never
          finished signing up (CG, Monday 13068863308). It sits under the
          campaign roll-up because the numbers above cannot answer it: Brevo
          reports that somebody clicked something, not who clicked what. */}
      <LinkClicksPanel />
    </div>
  );
}
